import useAuthUtilities from '@/AuthMiddleware/AuthUtilities';
import territoriesJson from '@/assets/territories.json';
import { IAlbum } from '@/data/models/album';
import {
  ErrorType,
  IValidationResults,
  ValidationLevel,
} from '@/data/models/validation';
import { isVideoFormat } from '@/utils/helper';
import { useCallback, useMemo } from 'react';

interface UseProductDetailsTableProps {
  errors: IValidationResults[];
  errorFlag: boolean;
  album: IAlbum;
}

export const useProductDetailsTable = ({
  errors,
  errorFlag,
  album,
}: UseProductDetailsTableProps) => {
  const {
    canUserViewInternalFields,
    canUserReadProjectService,
    canUserAccessProjectServiceUi,
  } = useAuthUtilities();

  const hasRequiredError = useCallback(
    (field: string): boolean => {
      const fieldError = errors.find((va) => va.fieldName === field);

      const fieldIsError = fieldError?.isValid === 'Error';
      const fieldIsAlbumError =
        fieldError?.validationLevel === ValidationLevel.Album;
      const fieldIsNullOrEmptyError =
        fieldError?.errorType === ErrorType.NullorEmpty;

      return (
        errorFlag &&
        fieldIsError &&
        fieldIsAlbumError &&
        fieldIsNullOrEmptyError
      );
    },
    [errorFlag, errors]
  );

  const isVideoProduct = useCallback(
    () => isVideoFormat(album.format?.name),
    [album.format?.name]
  );

  const albumCountryOfManufactureDisplayName = useMemo(
    () =>
      territoriesJson.find(
        (t) =>
          t.Reference ==
          album.albumMetaData?.manufacturing?.countryOfManufacture
      )?.DisplayName,
    [album.albumMetaData?.manufacturing?.countryOfManufacture]
  );

  return {
    canUserViewInternalFields,
    canUserReadProjectService,
    canUserAccessProjectServiceUi,
    hasRequiredError,
    isVideoProduct,
    albumCountryOfManufactureDisplayName,
  };
};
